import { bool, func, object, shape } from 'prop-types';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import styled from 'styled-components';

import Typography from 'ravenjs/lib/Typography';
import Button from 'ravenjs/lib/Button';
import { get, isEmpty } from 'ravenjs/utils/lodash';

import FilterContent from 'components/FilterContent';
import { DEFAULT_COLORS } from 'constants/colors';
import { MODALS } from 'constants/modals';
import { actions as uiActions, selectors as uiSelectors } from 'modules/ui';
import Modal from '../Modal';

const ActionButtons = styled.div`
    align-items: center;
    display: flex;
    justify-content: flex-end;
    margin: 1.5rem 0;
    width: 100%;
`;

const ApplyButton = styled.div`
    background-color: ${DEFAULT_COLORS.BLUE};
    color: ${DEFAULT_COLORS.BLACK};
    cursor: pointer;
    font-size: 0.812rem;
    padding: 0.563rem 0;
    text-align: center;
    width: 160px;
`;

const ResetButton = styled.div`
    color: ${DEFAULT_COLORS.BLACK};
    cursor: pointer;
    display: inline-block;
    font-size: 0.812rem;
    margin-right: 2.5rem;
    padding: 0.563rem 0;
    text-align: center;
    text-decoration: underline;
`;

const FilterModal = props => {
    const { closeModal, modal } = props;
    const open = get(modal, 'open', false);
    const buttonProps = get(modal, 'data.buttonProps', {});
    const filters = get(modal, 'data.filters', []);
    const value = get(modal, 'data.value');
    const onChangeFilter = get(modal, 'data.onChangeFilter');
    const handlers = get(modal, 'data.handlers');
    const onResetFilter = get(modal, 'data.onResetFilter', null);
    const onSubmitFilter = get(modal, 'data.onSubmitFilter');
    const [filterValue, setFilterValue] = useState(value);

    const onChangeFilters = (updatedFilterObject, ...rest) => {
        setFilterValue(updatedFilterObject);
        onChangeFilter({ ...rest, setFilterValue, updatedFilterObject });
    };

    const onClickCloseIcon = () => {
        closeModal(MODALS.FILTER);
    };

    const onClickReset = () => {
        onResetFilter({ setFilterValue });
        closeModal(MODALS.FILTER);
    };

    const onSubmit = () => {
        onSubmitFilter(filterValue);
        closeModal(MODALS.FILTER);
    };

    const renderButton = () => {
        return (
            <>
                {buttonProps.tooltipText && (
                    <Typography type="body">{buttonProps.tooltipText}</Typography>
                )}
                <Button
                    disabled={
                        typeof buttonProps.disabled === 'function'
                            ? buttonProps.disabled()
                            : buttonProps.disabled
                    }
                    onClick={buttonProps.onClick}
                    width="100%"
                >
                    {buttonProps.label}
                </Button>
            </>
        );
    };

    const renderContent = () => {
        return (
            <>
                <FilterContent
                    filters={filters}
                    value={filterValue}
                    onChangeFilters={onChangeFilters}
                    handlers={handlers}
                />
                {!isEmpty(buttonProps) && buttonProps.label && renderButton()}
                <ActionButtons>
                    {onResetFilter && (
                        <ResetButton onClick={onClickReset}>
                            <span>RESET</span>
                        </ResetButton>
                    )}
                    <ApplyButton onClick={onSubmit}>APPLY</ApplyButton>
                </ActionButtons>
            </>
        );
    };

    useEffect(() => {
        if (open) {
            setFilterValue(value);
        }
    }, [open, value]);

    return (
        <Modal
            id={MODALS.FILTER}
            content={renderContent()}
            hideActions
            modalBodyProps={{
                backgroundColor: DEFAULT_COLORS.COMPONENTS_THE_GREY_STYLE,
                padding: '0 15px 15px',
            }}
            modalHeaderProps={{
                backgroundColor: DEFAULT_COLORS.COMPONENTS_THE_GREY_STYLE,
                fontFamily: 'Roboto',
                fontSize: '1.375rem',
                removeBorderBottom: true,
            }}
            ModalProps={{
                ContentProps: {
                    style: {
                        borderRadius: 0,
                        height: '100%',
                        maxHeight: '100%',
                        maxWidth: '100%',
                        top: 0,
                    },
                },
            }}
            onClickCloseIcon={onClickCloseIcon}
            open={open}
            title="Filters"
        />
    );
};

FilterModal.propTypes = {
    closeModal: func.isRequired,
    modal: shape({
        open: bool,
        data: object,
    }).isRequired,
};

const mapStateToProps = () =>
    createStructuredSelector({
        modal: uiSelectors.getModalById(MODALS.FILTER),
    });

const mapDispatchToProps = {
    closeModal: uiActions.closeModal,
};

export { FilterModal as FilterModalUnwrapped };
export default connect(mapStateToProps, mapDispatchToProps)(FilterModal);
